import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, User, Menu, X, ChevronRight, ChevronLeft, ChevronUp, MapPin, Phone, Mail, Facebook, Twitter, Linkedin, Youtube, ArrowRight, Clock, Shield, Users, Award, Globe, Calendar, MessageSquare, Server, Lock, Network, Cloud, Database, Code, Cpu, Gauge, Languages } from 'lucide-react';
import ServicesHome from './ServicesHome';
import ServiceDetail from './ServiceDetail';
import AboutUs from './AboutUs';
import OurTeam from './OurTeam';
import Contact from './Contact';
import Solutions from './Solutions';
import Industries from './Industries';
import { fetchServices } from '../../services/wpApi';
import { mapWordPressServicesToServices, Service } from '../../utils/serviceMapper.tsx';
import { getAllMainCategories } from '../../utils/serviceCategories';
type Expert = {
  id: number;
  name: string;
  nameAr: string;
  title: string;
  titleAr: string;
  image: string;
  expertise: string;
  experience: string;
};
// Services will be loaded from WordPress API
const experts: Expert[] = [{
  id: 1,
  name: 'Dr. Ahmed Al Busaidi',
  nameAr: 'د. أحمد البوسعيدي',
  title: 'Chief Technology Architect',
  titleAr: 'كبير مهندسي التكنولوجيا',
  image: 'https://ui-avatars.com/api/?name=Ahmed+Al+Busaidi&background=006847&color=fff&size=512&font-size=0.33',
  expertise: 'Microservices & Cloud',
  experience: '20+ years'
}, {
  id: 2,
  name: 'Fatima Al Lawati',
  nameAr: 'فاطمة اللواتية',
  title: 'Cybersecurity Director',
  titleAr: 'مديرة الأمن السيبراني',
  image: 'https://ui-avatars.com/api/?name=Fatima+Al+Lawati&background=C8102E&color=fff&size=512&font-size=0.33',
  expertise: 'Security & Compliance',
  experience: '15+ years'
}, {
  id: 3,
  name: 'Khalid Al Hashmi',
  nameAr: 'خالد الهاشمي',
  title: 'Solutions Architect',
  titleAr: 'مهندس الحلول',
  image: 'https://ui-avatars.com/api/?name=Khalid+Al+Hashmi&background=D4AF37&color=fff&size=512&font-size=0.33',
  expertise: 'Enterprise Architecture',
  experience: '18+ years'
}, {
  id: 4,
  name: 'Sara Al Hinai',
  nameAr: 'سارة الحناوية',
  title: 'DevOps Lead',
  titleAr: 'قائدة DevOps',
  image: 'https://ui-avatars.com/api/?name=Sara+Al+Hinai&background=006847&color=fff&size=512&font-size=0.33',
  expertise: 'Automation & CI/CD',
  experience: '12+ years'
}];
const bannerSlides = [{
  id: 1,
  title: 'Strategic IT Excellence',
  titleAr: 'التميز في استراتيجية تكنولوجيا المعلومات',
  subtitle: 'Premier IT consulting and cybersecurity services in Oman',
  subtitleAr: 'خدمات استشارية رائدة في تكنولوجيا المعلومات والأمن السيبراني',
  image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&h=500&fit=crop',
  cta: 'Consult Now',
  color: '#007A3D'
}, {
  id: 2,
  title: 'Microservices Governance',
  titleAr: 'حوكمة الخدمات الصغيرة',
  subtitle: 'Scalable architecture solutions for modern enterprises',
  subtitleAr: 'حلول بنية قابلة للتطوير للشركات الحديثة',
  image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&h=500&fit=crop',
  cta: 'Our Services',
  color: '#007A3D'
}, {
  id: 3,
  title: 'Cybersecurity Leadership',
  titleAr: 'قيادة الأمن السيبراني',
  subtitle: 'Protecting your digital assets with advanced security',
  subtitleAr: 'حماية أصولك الرقمية بأمان متقدم',
  image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1200&h=500&fit=crop',
  cta: 'Secure Now',
  color: '#007A3D'
}];
const bareeqColors = {
  primary: '#007A3D', // Bareeq Tech Green
  secondary: '#D7182A', // Bareeq Tech Red
  accent: '#005BFF', // Bareeq Tech Blue
  gold: '#D4AF37', // Keep gold for accents
  dark: '#1A1A1A',
  light: '#F8F9FA',
  gray: '#6B7280'
};
type ViewMode = 'home' | 'services' | 'service-detail' | 'about' | 'team' | 'contact' | 'solutions' | 'industries';
type Language = 'en' | 'ar';
interface SelectedService {
  id: number;
  name: string;
  nameAr: string;
  description: string;
  longDescription: string;
  icon: React.ReactNode;
  color: string;
  features: string[];
  benefits: string[];
  slug: string;
}
const Header = ({
  onMenuToggle,
  isMenuOpen,
  onNavigate,
  language,
  onLanguageToggle,
  onSearch
}: {
  onMenuToggle: () => void;
  isMenuOpen: boolean;
  onNavigate: (view: ViewMode) => void;
  language: Language;
  onLanguageToggle: () => void;
  onSearch?: (query: string) => void;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      if (onSearch) {
        onSearch(searchQuery.trim());
      } else {
        // Default: navigate to services page
        onNavigate('services');
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  const t = {
    en: {
      phone: '+968 7760 4646',
      email: 'info@bareeqtech.net',
      location: 'PO BOX 406, PC100, Bawshar, Muscat, Sultanate of Oman',
      search: 'Search services, expertise...',
      searchBtn: 'Search',
      bookConsult: 'Book Consultation',
      nav: {
        home: 'Home',
        about: 'About Us',
        services: 'Services',
        team: 'Our Team',
        solutions: 'Solutions',
        industries: 'Industries',
        contact: 'Contact'
      }
    },
    ar: {
      phone: '+968 7760 4646',
      email: 'info@bareeqtech.net',
      location: 'ص.ب 406، PC100، بوشر، مسقط، سلطنة عمان',
      search: 'ابحث عن الخدمات',
      searchBtn: 'بحث',
      bookConsult: 'احجز استشارة',
      nav: {
        home: 'الرئيسية',
        about: 'من نحن',
        services: 'الخدمات',
        team: 'فريقنا',
        solutions: 'الحلول',
        industries: 'القطاعات',
        contact: 'اتصل بنا'
      }
    }
  };
  const text = t[language];
  return <header className={`sticky top-0 z-50 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="bg-gradient-to-r from-[#007A3D] to-[#005B2A] text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Phone size={14} />
              <span>{text.phone}</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <Mail size={14} />
              <span>{text.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={onLanguageToggle} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Languages size={16} />
              <span className="hidden sm:inline">{language === 'en' ? '🇴🇲 عربي' : '🇴🇲 English'}</span>
            </button>
            <div className="flex items-center gap-1">
              <MapPin size={14} />
              <span className="hidden sm:inline">{text.location}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={`max-w-7xl mx-auto px-4 transition-all duration-300 border-b border-gray-100/10 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-lg py-2' : 'bg-white py-4 shadow-sm'}`}>
        <div className="flex items-center justify-between gap-4">
          <button onClick={onMenuToggle} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="flex items-center gap-2">
            <button onClick={() => onNavigate('home')} className="flex items-center gap-2 hover:opacity-90 transition-opacity text-left">
              <img src="/Bareeq_site_logo.png" alt="Bareeq Tech" className={`w-auto object-contain ${isScrolled ? 'h-10' : 'h-12'}`} />
            </button>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-[#007A3D]">Bareeq Tech</h1>
              <p className="text-xs text-gray-500">بارييق تك</p>
            </div>
          </div>

          <div className="flex-1 max-w-2xl hidden md:block">
            <div className="relative">
              <input 
                type="text" 
                placeholder={text.search} 
                value={searchQuery} 
                onChange={e => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-3 pl-12 border-2 border-gray-200 rounded-full focus:border-[#006847] focus:outline-none transition-colors" 
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <button 
                onClick={handleSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#007A3D] text-white px-6 py-2 rounded-full hover:bg-[#005B2A] transition-colors"
              >
                {text.searchBtn}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <User size={24} className="text-gray-600" />
            </button>
            <button className="hidden sm:flex items-center gap-2 bg-[#007A3D] text-white px-6 py-2 rounded-full hover:bg-[#005B2A] transition-colors" onClick={() => onNavigate('contact')}>
              <Calendar size={20} />
              <span>{text.bookConsult}</span>
            </button>
          </div>
        </div>

        <div className="md:hidden mt-4">
          <div className="relative">
            <input 
              type="text" 
              placeholder={text.search} 
              value={searchQuery} 
              onChange={e => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-2 pl-10 border-2 border-gray-200 rounded-full focus:border-[#007A3D] focus:outline-none" 
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <button 
              onClick={handleSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#007A3D] text-white px-3 py-1.5 rounded-full hover:bg-[#005B2A] transition-colors text-sm"
            >
              {text.searchBtn}
            </button>
          </div>
        </div>
      </div>

      <nav className="hidden lg:block bg-gray-50 border-t">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center gap-8 py-3">
            <li>
              <button onClick={() => onNavigate('home')} className="text-gray-700 hover:text-[#006847] font-medium transition-colors">
                {text.nav.home}
              </button>
            </li>
            <li>
              <button onClick={() => onNavigate('about')} className="text-gray-700 hover:text-[#006847] font-medium transition-colors">
                {text.nav.about}
              </button>
            </li>
            <li>
              <button onClick={() => onNavigate('services')} className="text-gray-700 hover:text-[#006847] font-medium transition-colors">
                {text.nav.services}
              </button>
            </li>
            <li>
              <button onClick={() => onNavigate('team')} className="text-gray-700 hover:text-[#006847] font-medium transition-colors">
                {text.nav.team}
              </button>
            </li>
            <li>
              <button onClick={() => onNavigate('solutions')} className="text-gray-700 hover:text-[#006847] font-medium transition-colors">
                {text.nav.solutions}
              </button>
            </li>
            <li>
              <button onClick={() => onNavigate('industries')} className="text-gray-700 hover:text-[#006847] font-medium transition-colors">
                {text.nav.industries}
              </button>
            </li>
            <li>
              <button onClick={() => onNavigate('contact')} className="text-gray-700 hover:text-[#006847] font-medium transition-colors">
                {text.nav.contact}
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </header>;
};
const HeroBanner = ({
  onNavigate,
  language
}: {
  onNavigate?: (view: ViewMode) => void;
  language: Language;
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % bannerSlides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + bannerSlides.length) % bannerSlides.length);
  React.useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);
  const handleCTAClick = (cta: string) => {
    if (cta === 'Consult Now' || cta === 'Secure Now') {
      onNavigate?.('contact');
    } else if (cta === 'Our Services') {
      onNavigate?.('services');
    }
  };
  return <section className={`relative overflow-hidden ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <AnimatePresence mode="wait">
        <motion.div key={currentSlide} initial={{
        opacity: 0,
        x: 100
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -100
      }} transition={{
        duration: 0.5
      }} className="relative h-[300px] sm:h-[400px] md:h-[500px]">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundColor: bannerSlides[currentSlide].color,
          backgroundImage: `url(${bannerSlides[currentSlide].image})`
        }}>
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center">
            <div className="max-w-lg text-white">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-lg sm:text-xl mb-2 text-[#D4AF37]">
                {language === 'ar' ? bannerSlides[currentSlide].subtitleAr : bannerSlides[currentSlide].subtitle}
              </motion.p>
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                {language === 'ar' ? bannerSlides[currentSlide].titleAr : bannerSlides[currentSlide].title}
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="text-base sm:text-lg mb-6 text-gray-200">
                {language === 'ar' ? bannerSlides[currentSlide].subtitleAr : bannerSlides[currentSlide].subtitle}
              </motion.p>
              <motion.button initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} onClick={() => handleCTAClick(bannerSlides[currentSlide].cta)} className="bg-[#007A3D] hover:bg-[#00622F] text-white px-8 py-3 rounded-full font-semibold flex items-center gap-2 transition-colors">
                {language === 'ar' ? 'استشر الآن' : bannerSlides[currentSlide].cta}
                <ArrowRight size={20} />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors">
        <ChevronLeft size={24} className="text-[#007A3D]" />
      </button>
      <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors">
        <ChevronRight size={24} className="text-[#007A3D]" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {bannerSlides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-3 h-3 rounded-full transition-colors ${index === currentSlide ? 'bg-[#D4AF37]' : 'bg-white/60'}`} />)}
      </div>
    </section>;
};
const FeatureBanner = ({
  language
}: {
  language: Language;
}) => {
  const features = [{
    icon: Award,
    title: language === 'en' ? '20+ Years Experience' : 'أكثر من 20 عامًا',
    titleAr: 'أكثر من 20 عامًا',
    desc: language === 'en' ? 'Leading IT services in Oman' : 'خدمات تكنولوجيا معلومات رائدة'
  }, {
    icon: Users,
    title: language === 'en' ? '50+ IT Specialists' : '50+ متخصص تكنولوجيا',
    titleAr: '50+ متخصص تكنولوجيا',
    desc: language === 'en' ? 'Expert technical teams' : 'فرق فنية خبيرة'
  }, {
    icon: Globe,
    title: language === 'en' ? 'Regional Coverage' : 'تغطية إقليمية',
    titleAr: 'تغطية إقليمية',
    desc: language === 'en' ? 'Serving GCC enterprises' : 'نخدم شركات مجلس التعاون'
  }, {
    icon: Shield,
    title: language === 'en' ? 'Certified Security' : 'أمان معتمد',
    titleAr: 'أمان معتمد',
    desc: language === 'en' ? 'ISO 27001 compliant' : 'متوافق مع ISO 27001'
  }];
  return <section className="py-8 bg-white border-b">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {features.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} viewport={{
          once: true
        }} className="flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="w-12 h-12 bg-[#007A3D]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <feature.icon className="text-[#007A3D]" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">{feature.title}</h3>
                <p className="text-sm text-gray-500">{feature.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const ServiceSection = ({
  onNavigate,
  language,
  services
}: {
  onNavigate?: (view: ViewMode) => void;
  language: Language;
  services: Service[];
}) => {
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('all');
  const mainCategories = getAllMainCategories();

  // Filter services by main category
  const filteredServices = selectedMainCategory === 'all' 
    ? services 
    : services.filter(service => service.mainCategoryId === selectedMainCategory);

  // Count services per main category
  const getServiceCount = (categoryId: string) => {
    if (categoryId === 'all') return services.length;
    return services.filter(s => s.mainCategoryId === categoryId).length;
  };

  return <section className={`py-12 bg-gray-50 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
              {language === 'en' ? 'Our Services' : 'خدماتنا'}
            </h2>
            <p className="text-gray-500 mt-1">{language === 'en' ? 'خدماتنا' : 'Our Services'}</p>
          </div>
          <button onClick={() => onNavigate?.('services')} className="text-[#007A3D] hover:text-[#005B2A] font-medium flex items-center gap-1">
            {language === 'en' ? 'View All' : 'عرض الكل'} <ChevronRight size={18} />
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setSelectedMainCategory('all')}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              selectedMainCategory === 'all'
                ? 'bg-[#007A3D] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {language === 'en' ? `All Services (${services.length})` : `جميع الخدمات (${services.length})`}
          </button>
          {mainCategories.map((category) => {
            const count = getServiceCount(category.id);
            if (count === 0) return null;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedMainCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedMainCategory === category.id
                    ? 'bg-[#007A3D] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {language === 'en' ? `${category.name} (${count})` : `${category.nameAr} (${count})`}
              </button>
            );
          })}
        </div>

        {/* Services Grid */}
        {filteredServices.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {filteredServices.slice(0, 8).map((service, index) => <motion.div key={service.id} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: index * 0.05
          }} viewport={{
            once: true
          }} whileHover={{
            y: -5
          }} onClick={() => onNavigate?.('services')} className="bg-white rounded-2xl p-4 text-center cursor-pointer shadow-sm hover:shadow-lg transition-all">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3" style={{
              backgroundColor: `${service.color}20`
            }}>
                  {service.icon}
                </div>
                <h3 className="font-medium text-gray-800 text-sm">
                  {language === 'en' ? service.name : service.nameAr}
                </h3>
                <p className="text-xs text-gray-500">{language === 'en' ? service.nameAr : service.name}</p>
              </motion.div>)}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {language === 'en' ? 'No services found in this category' : 'لا توجد خدمات في هذه الفئة'}
          </div>
        )}
      </div>
    </section>;
};
const ExpertCard = ({
  expert,
  index,
  onNavigate,
  language
}: {
  expert: Expert;
  index: number;
  onNavigate?: (view: ViewMode) => void;
  language: Language;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.05
  }} viewport={{
    once: true
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all group">
      <div className="relative aspect-square overflow-hidden">
        <img src={expert.image} alt={expert.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: isHovered ? 1 : 0
      }} className="absolute inset-0 bg-gradient-to-t from-[#007A3D]/90 to-transparent flex items-end justify-center p-6">
          <button onClick={() => onNavigate?.('contact')} className="bg-white text-[#007A3D] px-6 py-2 rounded-full hover:bg-[#005BFF] hover:text-white transition-colors flex items-center gap-2">
            <MessageSquare size={18} />
            {language === 'en' ? 'Contact' : 'تواصل'}
          </button>
        </motion.div>
      </div>
      <div className="p-4">
        <p className="text-xs text-[#007A3D] font-medium mb-1">{expert.expertise}</p>
        <h3 className="font-semibold text-gray-800 mb-1">
          {language === 'en' ? expert.name : expert.nameAr}
        </h3>
        <p className="text-xs text-gray-500 mb-2">{language === 'en' ? expert.nameAr : expert.name}</p>
        <p className="text-sm text-gray-600 mb-2">
          {language === 'en' ? expert.title : expert.titleAr}
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Cpu size={14} />
          <span>{expert.experience} {language === 'en' ? 'experience' : 'خبرة'}</span>
        </div>
      </div>
    </motion.div>;
};
const ExpertsSection = ({
  onNavigate,
  language
}: {
  onNavigate?: (view: ViewMode) => void;
  language: Language;
}) => {
  return <section className={`py-12 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D7182A] rounded-full flex items-center justify-center">
              <Users className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
                {language === 'en' ? 'Our IT Experts' : 'خبراؤنا التقنيون'}
              </h2>
              <p className="text-gray-500">{language === 'en' ? 'خبراؤنا التقنيون' : 'Our IT Experts'}</p>
            </div>
          </div>
          <button onClick={() => onNavigate?.('team')} className="text-[#007A3D] hover:text-[#005B2A] font-medium flex items-center gap-1">
            {language === 'en' ? 'View All' : 'عرض الكل'} <ChevronRight size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {experts.map((expert, index) => <ExpertCard key={expert.id} expert={expert} index={index} onNavigate={onNavigate} language={language} />)}
        </div>
      </div>
    </section>;
};
const InsightsBanner = ({
  onNavigate,
  language
}: {
  onNavigate?: (view: ViewMode) => void;
  language: Language;
}) => {
  return <section className={`py-12 bg-gray-50 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative h-64 rounded-2xl overflow-hidden cursor-pointer group">
            <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=300&fit=crop" alt="Technical Insights" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#006847]/90 to-transparent flex items-center">
              <div className="p-8 text-white">
                <p className="text-[#D4AF37] font-medium mb-2 flex items-center gap-2">
                  <Code size={16} />
                  {language === 'en' ? 'Latest Insights' : 'أحدث الأفكار'}
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2">
                  {language === 'en' ? 'Technical Resources' : 'الموارد التقنية'}
                </h3>
                <p className="mb-4 text-white/80">
                  {language === 'en' ? 'Expert analysis and best practices' : 'تحليل الخبراء وأفضل الممارسات'}
                </p>
                <button onClick={() => onNavigate?.('solutions')} className="bg-white text-[#006847] px-6 py-2 rounded-full font-semibold hover:bg-[#D4AF37] hover:text-white transition-colors">
                  {language === 'en' ? 'Read More' : 'اقرأ المزيد'}
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative h-64 rounded-2xl overflow-hidden cursor-pointer group">
            <img src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=300&fit=crop" alt="Industries" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#C8102E]/90 to-transparent flex items-center">
              <div className="p-8 text-white">
                <p className="text-[#D4AF37] font-medium mb-2 flex items-center gap-2">
                  <Network size={16} />
                  {language === 'en' ? 'Industry Solutions' : 'حلول الصناعة'}
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2">
                  {language === 'en' ? 'Sector Expertise' : 'خبرة القطاع'}
                </h3>
                <p className="mb-4 text-white/80">
                  {language === 'en' ? 'Tailored IT solutions by industry' : 'حلول تكنولوجيا المعلومات المصممة حسب الصناعة'}
                </p>
                <button onClick={() => onNavigate?.('industries')} className="bg-white text-[#C8102E] px-6 py-2 rounded-full font-semibold hover:bg-[#D4AF37] hover:text-white transition-colors">
                  {language === 'en' ? 'Explore' : 'استكشف'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
const ConsultationCTA = ({
  onNavigate,
  language,
  services
}: {
  onNavigate?: (view: ViewMode) => void;
  language: Language;
  services: Service[];
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const handleBookConsultation = async () => {
    if (!formData.name || !formData.email) {
      setSubmitStatus('error');
      setSubmitMessage(language === 'en' ? 'Please enter your name and email.' : 'يرجى إدخال الاسم والبريد الإلكتروني.');
      return;
    }
    setSubmitStatus('sending');
    try {
      const res = await fetch('/wp-json/bareeq/v1/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'homepage-consultation' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitStatus('success');
        setSubmitMessage(language === 'en' ? 'Thank you — our team will contact you within 24 hours.' : 'شكراً لك — سيتواصل فريقنا معك خلال 24 ساعة.');
        setFormData({ name: '', email: '', phone: '', service: '' });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(data.message || (language === 'en' ? 'Something went wrong. Please try again.' : 'حدث خطأ ما. يرجى المحاولة مرة أخرى.'));
      }
    } catch (e) {
      setSubmitStatus('error');
      setSubmitMessage(language === 'en' ? 'Network error. Please try again.' : 'خطأ في الشبكة. يرجى المحاولة مرة أخرى.');
    }
  };
  return <section className={`py-16 bg-gradient-to-r from-[#007A3D] to-[#005B2A] ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }}>
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-6">
              <Calendar className="text-[#D4AF37]" size={32} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {language === 'en' ? 'Schedule a Technical Consultation' : 'حدد موعد استشارة تقنية'}
            </h2>
            <p className="text-white/80 mb-6">
              {language === 'en' ? 'Get expert IT guidance tailored to your business needs. Our team is ready to help you with comprehensive technology solutions.' : 'احصل على إرشادات تقنية خبيرة مصممة خصيصًا لاحتياجات عملك. فريقنا جاهز لمساعدتك بحلول تقنية شاملة.'}
            </p>
            <ul className="space-y-3 text-white/90">
              <li className="flex items-center gap-3">
                <Clock size={20} className="text-[#D4AF37]" />
                {language === 'en' ? 'Initial consultation: 30 minutes free' : 'استشارة أولية: 30 دقيقة مجانًا'}
              </li>
              <li className="flex items-center gap-3">
                <Shield size={20} className="text-[#D4AF37]" />
                {language === 'en' ? 'Confidential and secure' : 'سري وآمن'}
              </li>
              <li className="flex items-center gap-3">
                <Users size={20} className="text-[#D4AF37]" />
                {language === 'en' ? 'Matched with the right expert' : 'تطابق مع الخبير المناسب'}
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-2xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">
              {language === 'en' ? 'Book Your Consultation' : 'احجز استشارتك'}
            </h3>
            <div className="space-y-4">
              <input type="text" placeholder={language === 'en' ? 'Full Name' : 'الاسم الكامل'} value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#007A3D] focus:outline-none" />
              <input type="email" placeholder={language === 'en' ? 'Email Address' : 'عنوان البريد الإلكتروني'} value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#007A3D] focus:outline-none" />
              <input type="tel" placeholder={language === 'en' ? 'Phone Number' : 'رقم الهاتف'} value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#007A3D] focus:outline-none" />
              <select value={formData.service} onChange={e => setFormData({
              ...formData,
              service: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#006847] focus:outline-none">
                <option value="">{language === 'en' ? 'Select Service' : 'اختر الخدمة'}</option>
                {services.map(service => <option key={service.id} value={service.name}>
                    {language === 'en' ? service.name : service.nameAr}
                  </option>)}
              </select>
              <button disabled={submitStatus === 'sending'} className="w-full bg-[#007A3D] hover:bg-[#00622F] disabled:opacity-60 text-white px-8 py-3 rounded-full font-semibold transition-colors flex items-center justify-center gap-2" onClick={handleBookConsultation}>
                <Calendar size={20} />
                {submitStatus === 'sending' ? (language === 'en' ? 'Sending...' : 'جارٍ الإرسال...') : (language === 'en' ? 'Book Consultation' : 'احجز استشارة')}
              </button>
              {submitMessage && <p className={`text-sm mt-2 font-medium ${submitStatus === 'success' ? 'text-green-700' : 'text-red-600'}`}>{submitMessage}</p>}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
const Footer = ({
  onNavigate,
  language,
  services
}: {
  onNavigate?: (view: ViewMode) => void;
  language: Language;
  services: Service[];
}) => {
  return <footer className={`bg-gray-900 text-white ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/Bareeq_site_logo.png" alt="Bareeq Tech" className="h-12 w-auto object-contain" />
              <div>
                <h3 className="text-xl font-bold">Bareeq Tech</h3>
                <p className="text-xs text-gray-400">خدمات تكنولوجيا المعلومات</p>
              </div>
            </div>
            <p className="text-gray-400 mb-4">
              {language === 'en' ? 'Premier IT consulting firm in the Sultanate of Oman, providing comprehensive technology and cybersecurity solutions.' : 'شركة استشارات تقنية رائدة في سلطنة عمان، تقدم حلول تقنية وأمن سيبراني شاملة.'}
            </p>
            <div className="flex gap-3">
              {[Facebook, Twitter, Linkedin, Youtube].map((Icon, i) => <a key={i} href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#007A3D] transition-colors">
                  <Icon size={18} />
                </a>)}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">
              {language === 'en' ? 'Services' : 'الخدمات'}
            </h4>
            <ul className="space-y-2">
              {services.slice(0, 6).map(service => <li key={service.id}>
                  <button onClick={() => onNavigate?.('services')} className="text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
                    {language === 'en' ? service.name : service.nameAr}
                  </button>
                </li>)}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">
              {language === 'en' ? 'Quick Links' : 'روابط سريعة'}
            </h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => onNavigate?.('about')} className="text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
                  {language === 'en' ? 'About Us' : 'من نحن'}
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate?.('team')} className="text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
                  {language === 'en' ? 'Our Team' : 'فريقنا'}
                </button>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-[#D4AF37] transition-colors">
                  {language === 'en' ? 'Careers' : 'وظائف'}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-[#D4AF37] transition-colors">
                  {language === 'en' ? 'Case Studies' : 'دراسات الحالة'}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-[#D4AF37] transition-colors">
                  {language === 'en' ? 'News & Events' : 'الأخبار والفعاليات'}
                </a>
              </li>
              <li>
                <button onClick={() => onNavigate?.('contact')} className="text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
                  {language === 'en' ? 'Contact' : 'اتصل'}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">
              {language === 'en' ? 'Contact Info' : 'معلومات الاتصال'}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#006847] flex-shrink-0 mt-1" />
                <span className="text-gray-400">
                  {language === 'en' ? 'PO BOX 406, PC100, Bawshar, Muscat, Sultanate of Oman' : 'ص.ب 406، PC100، بوشر، مسقط، سلطنة عمان'}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#006847]" />
                <span className="text-gray-400">+968 7760 4646 | +968 7672 7225</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#006847]" />
                <span className="text-gray-400">info@bareeqtech.net</span>
              </li>
              <li className="flex items-center gap-3">
                <Clock size={18} className="text-[#006847]" />
                <span className="text-gray-400">
                  {language === 'en' ? 'Sun-Thu: 8:00 AM - 5:00 PM' : 'الأحد-الخميس: 8:00 ص - 5:00 م'}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              {language === 'en' ? '© 2026 Bareeq Tech. All rights reserved. | Empowering Secure Digital Transformation in Oman.' : '© 2026 بارييق تك. جميع الحقوق محفوظة. | تمكين التحول الرقمي الآمن في عمان.'}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <a href="#" className="hover:text-[#D4AF37] transition-colors">
                {language === 'en' ? 'Privacy Policy' : 'سياسة الخصوصية'}
              </a>
              <span>|</span>
              <a href="#" className="hover:text-[#D4AF37] transition-colors">
                {language === 'en' ? 'Terms of Service' : 'شروط الخدمة'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>;
};
const MobileMenu = ({
  isOpen,
  onClose,
  onNavigate,
  language
}: {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: ViewMode) => void;
  language: Language;
}) => {
  const handleNavigation = (view: ViewMode) => {
    onNavigate(view);
    onClose();
  };
  const navItems = {
    en: {
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      team: 'Our Team',
      solutions: 'Solutions',
      industries: 'Industries',
      contact: 'Contact',
      book: 'Book Consultation'
    },
    ar: {
      home: 'الرئيسية',
      about: 'من نحن',
      services: 'الخدمات',
      team: 'فريقنا',
      solutions: 'الحلول',
      industries: 'القطاعات',
      contact: 'اتصل بنا',
      book: 'احجز استشارة'
    }
  };
  const nav = navItems[language];
  return <AnimatePresence>
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50 lg:hidden" onClick={onClose} />
          <motion.div initial={{
        x: language === 'ar' ? '100%' : '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: language === 'ar' ? '100%' : '-100%'
      }} transition={{
        type: 'tween'
      }} className={`fixed ${language === 'ar' ? 'right-0' : 'left-0'} top-0 bottom-0 w-80 bg-white z-50 lg:hidden overflow-y-auto`}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#007A3D] to-[#D7182A] rounded-full flex items-center justify-center">
                  <Server className="text-white" size={16} />
                </div>
                <span className="font-bold text-[#007A3D]">Bareeq Tech</span>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>
            <nav className="p-4">
              <ul className="space-y-2">
                <li>
                  <button onClick={() => handleNavigation('home')} className="block w-full text-left py-3 px-4 rounded-lg hover:bg-gray-100 text-gray-700 font-medium">
                    {nav.home}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('about')} className="block w-full text-left py-3 px-4 rounded-lg hover:bg-gray-100 text-gray-700 font-medium">
                    {nav.about}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('services')} className="block w-full text-left py-3 px-4 rounded-lg hover:bg-gray-100 text-gray-700 font-medium">
                    {nav.services}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('team')} className="block w-full text-left py-3 px-4 rounded-lg hover:bg-gray-100 text-gray-700 font-medium">
                    {nav.team}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('solutions')} className="block w-full text-left py-3 px-4 rounded-lg hover:bg-gray-100 text-gray-700 font-medium">
                    {nav.solutions}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('industries')} className="block w-full text-left py-3 px-4 rounded-lg hover:bg-gray-100 text-gray-700 font-medium">
                    {nav.industries}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('contact')} className="block w-full text-left py-3 px-4 rounded-lg hover:bg-gray-100 text-gray-700 font-medium">
                    {nav.contact}
                  </button>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t">
                <button className="w-full bg-[#007A3D] text-white px-6 py-3 rounded-full hover:bg-[#005B2A] transition-colors flex items-center justify-center gap-2" onClick={() => handleNavigation('contact')}>
                  <Calendar size={20} />
                  {nav.book}
                </button>
              </div>
            </nav>
          </motion.div>
        </>}
    </AnimatePresence>;
};

// Scroll-to-top floating button (visible after scrolling)
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-6 z-40 bg-white text-[#006847] p-3 rounded-full shadow-lg border border-gray-100 hover:bg-gray-50"
          aria-label="Scroll to top"
        >
          <ChevronUp size={24} />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

// @component: OmaniEcommerce
interface OmaniEcommerceProps {
  initialView?: ViewMode;
  selectedService?: SelectedService | null;
  onNavigate?: (view: ViewMode, service?: any) => void;
}

const OmaniEcommerce = ({ 
  initialView = 'home',
  selectedService: initialSelectedService = null,
  onNavigate: externalNavigate
}: OmaniEcommerceProps = {}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentView, setCurrentView] = useState<ViewMode>(initialView);
  const [selectedService, setSelectedService] = useState<SelectedService | null>(initialSelectedService);
  const [language, setLanguage] = useState<Language>('en');
  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  
  // Load services from WordPress API
  useEffect(() => {
    const loadServices = async () => {
      try {
        setServicesLoading(true);
        const wpServices = await fetchServices();
        // Ensure wpServices is an array
        const servicesArray = Array.isArray(wpServices) ? wpServices : [];
        const mappedServices = mapWordPressServicesToServices(servicesArray);
        setServices(mappedServices);
      } catch (error: any) {
        console.error('Error loading services:', error);
        console.error('Error details:', {
          message: error?.message,
          stack: error?.stack
        });
        // Keep empty array on error - components will handle gracefully
        setServices([]);
      } finally {
        setServicesLoading(false);
      }
    };

    loadServices();
  }, []);

  // Sync with external initialView prop changes
  useEffect(() => {
    if (initialView !== currentView) {
      setCurrentView(initialView);
    }
  }, [initialView]);
  
  useEffect(() => {
    if (initialSelectedService !== selectedService) {
      setSelectedService(initialSelectedService);
    }
  }, [initialSelectedService]);
  
  const handleNavigate = (view: ViewMode, service?: any) => {
    setCurrentView(view);
    if (view !== 'service-detail') {
      setSelectedService(null);
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    // Call external navigate if provided
    if (externalNavigate) {
      externalNavigate(view, service);
    }
  };
  const handleServiceSelect = (service: any) => {
    setSelectedService(service);
    setCurrentView('service-detail');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    // Call external navigate if provided
    if (externalNavigate) {
      externalNavigate('service-detail', service);
    }
  };
  const handleLanguageToggle = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const [headerSearchQuery, setHeaderSearchQuery] = useState('');

  const handleHeaderSearch = (query: string) => {
    setHeaderSearchQuery(query);
    handleNavigate('services');
  };

  // @return
  return <div className="min-h-screen bg-gray-50">
      <Header 
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)} 
        isMenuOpen={isMenuOpen} 
        onNavigate={handleNavigate} 
        language={language} 
        onLanguageToggle={handleLanguageToggle}
        onSearch={handleHeaderSearch}
      />
      <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} onNavigate={handleNavigate} language={language} />

      <main>
        {currentView === 'home' && <>
            <HeroBanner onNavigate={handleNavigate} language={language} />
            <FeatureBanner language={language} />
            <ServiceSection onNavigate={handleNavigate} language={language} services={services} />
            <ExpertsSection onNavigate={handleNavigate} language={language} />
            <InsightsBanner onNavigate={handleNavigate} language={language} />
            <ConsultationCTA onNavigate={handleNavigate} language={language} services={services} />
          </>}

        {currentView === 'services' && <ServicesHome onServiceSelect={handleServiceSelect} onConsultClick={() => handleNavigate('contact')} initialSearchQuery={headerSearchQuery} />}

        {currentView === 'service-detail' && selectedService && <ServiceDetail service={selectedService} onBack={() => handleNavigate('services')} onGetStartedClick={() => handleNavigate('contact')} onConsultClick={() => handleNavigate('contact')} />}

        {currentView === 'about' && <AboutUs onBack={() => handleNavigate('home')} />}

        {currentView === 'team' && <OurTeam onBack={() => handleNavigate('home')} onNavigate={handleNavigate} />}

        {currentView === 'contact' && <Contact onBack={() => handleNavigate('home')} />}

        {currentView === 'solutions' && <Solutions onBack={() => handleNavigate('home')} />}

        {currentView === 'industries' && <Industries onBack={() => handleNavigate('home')} />}
      </main>

      <Footer onNavigate={handleNavigate} language={language} services={services} />
      <ScrollToTop />
    </div>;
};

export default OmaniEcommerce;